import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  BookOpenText,
  ChevronDown,
  Clock,
  Images,
  MessageSquareText,
  Package,
  Palette,
  Save,
  ShieldAlert,
  Sparkles,
  Store,
  Users,
  UsersRound,
  Wand2,
} from "lucide-react";
import {
  IMAGE_PEOPLE_OPTIONS,
  PRICE_POSITIONS,
  TONE_PRESETS,
  formatTone,
  parseTone,
  type BrandProfileInputDto,
  type BrandVoiceInputDto,
  type TonePreset,
} from "@marketing/shared";
import { apiErrorMessage } from "@/lib/api";
import { useBrands } from "@/lib/queries";
import {
  useProfile,
  usePreviewVoice,
  useSaveProfile,
  useSaveVoice,
  useVoices,
} from "@/lib/content";
import { Card, Notice, PageHeader, SkeletonRows, Spinner } from "@/components/ui";
import { ProductCatalogue } from "@/components/ProductCatalogue";
import { ErrorBox, Field } from "./LoginPage";

// =============================================================================
// Brand voice: everything the generator knows about a brand, on one screen.
//
// The facts and the manner of speaking were two screens and are now one,
// because the split asked the owner to understand our data model. They think
// of it as "tell the thing about my business"; that it lands in two tables is
// our business, not theirs.
//
// The two tables remain, and the distinction still matters underneath: a voice
// can be overridden per connected page, a fact cannot. One Save writes both.
//
// Sections are collapsed except the two that matter most on a first visit, so
// twenty questions do not arrive as one wall. Nothing is required.
// =============================================================================

const EMPTY_VOICE: BrandVoiceInputDto = {
  socialAccountId: null,
  tone: "",
  audience: "",
  language: "en",
  dialectNotes: "",
  wordsToUse: [],
  wordsToAvoid: [],
  samplePosts: [],
  emojiLevel: "some",
  hashtagStyle: "few",
};

const EMPTY_PROFILE: BrandProfileInputDto = {
  imagePeople: "local",
  primaryColor: "",
  supportingColors: [],
  photographyStyle: "",
  description: "",
  story: "",
  mission: "",
  differentiators: [],
  pricePositioning: null,
  products: [],
  bestSellers: [],
  customerNeeds: "",
  objections: [],
  location: "",
  serviceArea: "",
  openingHours: "",
  contactPhone: "",
  website: "",
  deliveryInfo: "",
  paymentMethods: [],
  preferredCta: "",
  topicsToAvoid: [],
  complianceNotes: "",
  factSheet: "",
};

/**
 * Counted for the "answered" nudge; mirrors profileService.ts.
 *
 * Settings that always hold a value are excluded. imagePeople defaults to
 * "local", so counting it would mean every brand starts at 1 of 21 answered
 * for a question nobody has looked at.
 */
const PROFILE_KEYS = (Object.keys(EMPTY_PROFILE) as Array<keyof BrandProfileInputDto>).filter(
  (k) => k !== "imagePeople" && k !== "pricePositioning",
);

export function VoicePage() {
  const { t } = useTranslation();
  const brands = useBrands();
  const [brandId, setBrandId] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId && brands.data?.length) setBrandId(brands.data[0]!.id);
  }, [brands.data, brandId]);

  const voices = useVoices(brandId);
  const profile = useProfile(brandId);
  const saveVoice = useSaveVoice(brandId);
  const saveProfile = useSaveProfile(brandId);
  const preview = usePreviewVoice();

  const [voice, setVoice] = useState<BrandVoiceInputDto>(EMPTY_VOICE);
  const [facts, setFacts] = useState<BrandProfileInputDto>(EMPTY_PROFILE);
  const [topic, setTopic] = useState("Introduce the business");
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Load the brand default (socialAccountId null) when the brand changes.
  useEffect(() => {
    const current = voices.data?.find((v) => v.socialAccountId === null);
    setVoice(
      current
        ? {
            socialAccountId: null,
            tone: current.tone ?? "",
            audience: current.audience ?? "",
            language: current.language,
            dialectNotes: current.dialectNotes ?? "",
            wordsToUse: current.wordsToUse,
            wordsToAvoid: current.wordsToAvoid,
            samplePosts: current.samplePosts,
            emojiLevel: current.emojiLevel,
            hashtagStyle: current.hashtagStyle,
          }
        : EMPTY_VOICE,
    );
    setSavedAt(null);
  }, [voices.data, brandId]);

  useEffect(() => {
    const row = profile.data;
    setFacts(row ? { ...EMPTY_PROFILE, ...stripNulls(row) } : EMPTY_PROFILE);
    setSavedAt(null);
  }, [profile.data, brandId]);

  const answered = useMemo(
    () =>
      PROFILE_KEYS.filter((key) => {
        const value = facts[key];
        if (Array.isArray(value)) return value.length > 0;
        return typeof value === "string" && value.trim().length > 0;
      }).length,
    [facts],
  );

  function setV<K extends keyof BrandVoiceInputDto>(key: K, value: BrandVoiceInputDto[K]) {
    setVoice((f) => ({ ...f, [key]: value }));
    setSavedAt(null);
  }

  function setF<K extends keyof BrandProfileInputDto>(key: K, value: BrandProfileInputDto[K]) {
    setFacts((f) => ({ ...f, [key]: value }));
    setSavedAt(null);
  }

  /**
   * One button, two records.
   *
   * Sequential rather than parallel so a failure names which half did not
   * save. Both are idempotent PUTs, so pressing Save again after a failure is
   * safe and simply rewrites whichever one already landed.
   */
  async function submit() {
    if (!brandId) return;
    setError(null);
    try {
      await saveProfile.mutateAsync({
        ...facts,
        // A blank select must reach the API as null, never as "".
        pricePositioning: facts.pricePositioning || null,
        imagePeople: facts.imagePeople || "local",
      });
      await saveVoice.mutateAsync(voice);
      setSavedAt(new Date());
    } catch (err) {
      setError(apiErrorMessage(err, t("errors.generic")));
    }
  }

  async function runPreview() {
    if (!brandId) return;
    setError(null);
    try {
      // Previews against the unsaved voice, so a change can be heard before
      // it is committed. The facts come from what is stored, because the
      // preview endpoint resolves them server side.
      await preview.mutateAsync({ brandId, voice, topic });
    } catch (err) {
      setError(apiErrorMessage(err, t("errors.generic")));
    }
  }

  const saving = saveProfile.isPending || saveVoice.isPending;
  const loading = voices.isLoading || profile.isLoading;

  if (!brands.isLoading && (brands.data?.length ?? 0) === 0) {
    return <Notice tone="warning">{t("voice.needBrand")}</Notice>;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<MessageSquareText size={18} />}
        title={t("voice.title")}
        subtitle={t("voice.subtitle")}
        actions={
          <select
            value={brandId ?? ""}
            onChange={(e) => setBrandId(e.target.value || null)}
            className="select w-full min-w-0 sm:w-auto sm:max-w-xs"
          >
            {(brands.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        }
      />

      <Notice tone="info">
        {t("profile.why")} {t("profile.answered", { answered, total: PROFILE_KEYS.length })}
      </Notice>

      {loading ? (
        <Card>
          <SkeletonRows rows={4} />
        </Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
          <div className="space-y-4">
            {/* ── How it should sound ── */}
            <Section
              icon={<MessageSquareText size={15} />}
              title={t("voice.soundTitle")}
              defaultOpen
            >
              <Field
                label={t("voice.language")}
                value={voice.language}
                onChange={(v) => setV("language", v)}
                hint={t("voice.languageHint")}
              />
              <TonePicker value={voice.tone ?? ""} onChange={(v) => setV("tone", v)} />
              <Field
                label={t("voice.audience")}
                value={voice.audience ?? ""}
                onChange={(v) => setV("audience", v)}
                hint={t("voice.audienceHint")}
              />
              <Field
                label={t("voice.dialect")}
                value={voice.dialectNotes ?? ""}
                onChange={(v) => setV("dialectNotes", v)}
                hint={t("voice.dialectHint")}
              />
              <ListField
                label={t("voice.wordsToUse")}
                value={voice.wordsToUse}
                onChange={(v) => setV("wordsToUse", v)}
                hint={t("voice.listHint")}
                separator="commas"
                rows={2}
              />
              <ListField
                label={t("voice.wordsToAvoid")}
                value={voice.wordsToAvoid}
                onChange={(v) => setV("wordsToAvoid", v)}
                hint={t("voice.listHint")}
                separator="commas"
                rows={2}
              />
              <ListField
                label={t("voice.samples")}
                hint={t("voice.samplesHint")}
                value={voice.samplePosts}
                onChange={(v) => setV("samplePosts", v)}
                rows={4}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <Choice
                  label={t("voice.emoji")}
                  value={voice.emojiLevel}
                  onChange={(v) => setV("emojiLevel", v as BrandVoiceInputDto["emojiLevel"])}
                  options={["none", "some", "lots"]}
                  labelFor={(o) => t(`voice.emojiLevels.${o}`)}
                />
                <Choice
                  label={t("voice.hashtags")}
                  value={voice.hashtagStyle}
                  onChange={(v) => setV("hashtagStyle", v as BrandVoiceInputDto["hashtagStyle"])}
                  options={["none", "few", "many"]}
                  labelFor={(o) => t(`voice.hashtagStyles.${o}`)}
                />
              </div>
            </Section>

            {/* ── About the business ── */}
            <Section icon={<Store size={15} />} title={t("profile.about.title")} defaultOpen>
              <Text
                label={t("profile.about.description")}
                hint={t("profile.about.descriptionHint")}
                value={facts.description ?? ""}
                onChange={(v) => setF("description", v)}
                rows={2}
              />
              <Text
                label={t("profile.about.mission")}
                hint={t("profile.about.missionHint")}
                value={facts.mission ?? ""}
                onChange={(v) => setF("mission", v)}
                rows={2}
              />
              <Text
                label={t("profile.about.story")}
                hint={t("profile.about.storyHint")}
                value={facts.story ?? ""}
                onChange={(v) => setF("story", v)}
                rows={3}
              />
              <ListField
                label={t("profile.about.differentiators")}
                hint={t("profile.listHint")}
                value={facts.differentiators}
                onChange={(v) => setF("differentiators", v)}
              />
              <label className="block">
                <span className="label">{t("profile.about.price")}</span>
                <select
                  value={facts.pricePositioning ?? ""}
                  onChange={(e) =>
                    setF(
                      "pricePositioning",
                      (e.target.value || null) as BrandProfileInputDto["pricePositioning"],
                    )
                  }
                  className="select"
                >
                  <option value="">{t("profile.about.priceUnset")}</option>
                  {PRICE_POSITIONS.map((p) => (
                    <option key={p} value={p}>
                      {t(`profile.about.priceLevels.${p}`)}
                    </option>
                  ))}
                </select>
                <span className="hint">{t("profile.about.priceHint")}</span>
              </label>
            </Section>

            {/* ── What you sell ── */}
            <Section icon={<Package size={15} />} title={t("profile.offering.title")}>
              <ListField
                label={t("profile.offering.products")}
                hint={t("profile.listHint")}
                value={facts.products}
                onChange={(v) => setF("products", v)}
              />
              <ListField
                label={t("profile.offering.bestSellers")}
                hint={t("profile.offering.bestSellersHint")}
                value={facts.bestSellers}
                onChange={(v) => setF("bestSellers", v)}
              />
            </Section>

            {/* ── The products, with photographs of them ── */}
            <Section icon={<Images size={15} />} title={t("catalogue.title")}>
              <ProductCatalogue brandId={brandId} />
            </Section>

            {/* ── Customers ── */}
            <Section icon={<Users size={15} />} title={t("profile.customers.title")}>
              <Text
                label={t("profile.customers.needs")}
                hint={t("profile.customers.needsHint")}
                value={facts.customerNeeds ?? ""}
                onChange={(v) => setF("customerNeeds", v)}
                rows={3}
              />
              <ListField
                label={t("profile.customers.objections")}
                hint={t("profile.customers.objectionsHint")}
                value={facts.objections}
                onChange={(v) => setF("objections", v)}
              />
            </Section>

            {/* ── Statable facts ── */}
            <Section icon={<Clock size={15} />} title={t("profile.facts.title")}>
              <p className="-mt-1 text-xs text-muted">{t("profile.facts.hint")}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={t("profile.facts.location")}
                  value={facts.location ?? ""}
                  onChange={(v) => setF("location", v)}
                />
                <Field
                  label={t("profile.facts.serviceArea")}
                  value={facts.serviceArea ?? ""}
                  onChange={(v) => setF("serviceArea", v)}
                />
                <Field
                  label={t("profile.facts.hours")}
                  value={facts.openingHours ?? ""}
                  onChange={(v) => setF("openingHours", v)}
                />
                <Field
                  label={t("profile.facts.phone")}
                  value={facts.contactPhone ?? ""}
                  onChange={(v) => setF("contactPhone", v)}
                />
                <Field
                  label={t("profile.facts.website")}
                  value={facts.website ?? ""}
                  onChange={(v) => setF("website", v)}
                />
                <Field
                  label={t("profile.facts.cta")}
                  hint={t("profile.facts.ctaHint")}
                  value={facts.preferredCta ?? ""}
                  onChange={(v) => setF("preferredCta", v)}
                />
              </div>
              <Text
                label={t("profile.facts.delivery")}
                value={facts.deliveryInfo ?? ""}
                onChange={(v) => setF("deliveryInfo", v)}
                rows={2}
              />
              <ListField
                label={t("profile.facts.payment")}
                hint={t("profile.listHint")}
                value={facts.paymentMethods}
                onChange={(v) => setF("paymentMethods", v)}
              />
            </Section>

            {/* ── Who appears in generated images ── */}
            <Section icon={<UsersRound size={15} />} title={t("profile.people.title")} defaultOpen>
              <p className="-mt-1 text-xs text-muted">{t("profile.people.hint")}</p>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {IMAGE_PEOPLE_OPTIONS.map((option) => {
                  const on = (facts.imagePeople ?? "local") === option;
                  return (
                    <label
                      key={option}
                      className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-[13px] transition-colors ${
                        on ? "border-primary-300 bg-primary-50" : "border-border hover:bg-surface"
                      }`}
                    >
                      <input
                        type="radio"
                        name="imagePeople"
                        checked={on}
                        onChange={() => setF("imagePeople", option)}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block font-medium text-ink">
                          {t(`profile.people.options.${option}`)}
                        </span>
                        {option === "local" ? (
                          <span className="block text-xs text-muted">
                            {t("profile.people.localHint")}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </Section>

            {/* ── How the pictures should look ──
                Steers image generation only. Captions never read these, which
                is why it sits beside "who appears in our pictures" rather
                than with the facts a post may state. */}
            <Section icon={<Palette size={15} />} title={t("profile.visual.title")}>
              <Text
                label={t("profile.visual.primaryColor")}
                hint={t("profile.visual.primaryColorHint")}
                value={facts.primaryColor ?? ""}
                onChange={(v) => setF("primaryColor", v)}
              />
              <ListField
                label={t("profile.visual.supportingColors")}
                hint={t("profile.visual.supportingColorsHint")}
                value={facts.supportingColors}
                onChange={(v) => setF("supportingColors", v)}
              />
              <Text
                label={t("profile.visual.photographyStyle")}
                hint={t("profile.visual.photographyStyleHint")}
                value={facts.photographyStyle ?? ""}
                onChange={(v) => setF("photographyStyle", v)}
                rows={2}
              />
            </Section>

            {/* ── Hard limits ── */}
            <Section icon={<ShieldAlert size={15} />} title={t("profile.limits.title")}>
              <ListField
                label={t("profile.limits.topics")}
                hint={t("profile.limits.topicsHint")}
                value={facts.topicsToAvoid}
                onChange={(v) => setF("topicsToAvoid", v)}
              />
              <Text
                label={t("profile.limits.compliance")}
                hint={t("profile.limits.complianceHint")}
                value={facts.complianceNotes ?? ""}
                onChange={(v) => setF("complianceNotes", v)}
                rows={3}
              />
            </Section>

            {/* ── Free-text fact sheet ── */}
            <Section icon={<BookOpenText size={15} />} title={t("profile.sheet.title")} defaultOpen>
              <Text
                label={t("profile.sheet.label")}
                hint={t("profile.sheet.hint")}
                value={facts.factSheet ?? ""}
                onChange={(v) => setF("factSheet", v)}
                rows={10}
                max={5000}
              />
            </Section>

            {error ? <ErrorBox>{error}</ErrorBox> : null}

            <div className="sticky bottom-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-white/90 px-4 py-3 shadow-lift backdrop-blur">
              <button onClick={() => void submit()} disabled={saving} className="btn-primary">
                {saving ? <Spinner /> : <Save size={15} />}
                {saving ? t("voice.saving") : t("voice.save")}
              </button>
              <span className="text-xs text-muted">
                {savedAt ? t("voice.savedAt", { time: savedAt.toLocaleTimeString() }) : ""}
              </span>
            </div>
          </div>

          {/* ── Live preview ── */}
          <aside className="lg:sticky lg:top-24">
            <Card>
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-primary-600" />
                <h2 className="text-sm font-semibold text-ink">{t("voice.previewTitle")}</h2>
              </div>
              <p className="mt-1 text-xs text-muted">{t("voice.previewHint")}</p>

              <div className="mt-4 space-y-3">
                <Field label={t("voice.previewTopic")} value={topic} onChange={setTopic} />
                <button
                  onClick={() => void runPreview()}
                  disabled={preview.isPending || !brandId}
                  className="btn-secondary w-full"
                >
                  {preview.isPending ? <Spinner /> : <Wand2 size={14} />}
                  {preview.isPending ? t("voice.previewing") : t("voice.previewRun")}
                </button>
              </div>

              {preview.data ? (
                <ul className="mt-4 space-y-3">
                  {preview.data.variants.map((v) => (
                    <li key={v.platform} className="rounded-xl bg-surface p-3">
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">
                        {v.platform}
                      </p>
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                        {v.caption}
                      </p>
                      {v.hashtags.length > 0 ? (
                        <p className="mt-2 text-xs text-primary-700">
                          {v.hashtags.map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Card>
          </aside>
        </div>
      )}
    </div>
  );
}

/** Collapsible so twenty questions do not arrive as one wall. */
function Section({
  icon,
  title,
  children,
  defaultOpen = false,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="card overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-5 py-4 text-start transition-colors hover:bg-surface"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
          {icon}
        </span>
        <span className="flex-1 text-sm font-semibold text-ink">{title}</span>
        <ChevronDown
          size={16}
          className={`text-muted transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? <div className="space-y-4 border-t border-border p-5">{children}</div> : null}
    </section>
  );
}

function Text({
  label,
  hint,
  value,
  onChange,
  rows = 3,
  max,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  max?: number;
}) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <textarea
        value={value}
        rows={rows}
        maxLength={max}
        onChange={(e) => onChange(e.target.value)}
        className="input resize-y"
      />
      <span className="hint">
        {hint}
        {max ? (
          <span className="float-end tabular-nums">
            {value.length} / {max}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/**
 * A list field you can actually type in.
 *
 * The previous version rendered `value.join("\n")` and parsed every keystroke
 * with `.map(trim).filter(Boolean)`. That round trip ate the character you
 * had just typed: pressing Enter produced a trailing empty entry which was
 * filtered away and joined back to the same string, so a new line could never
 * be started, and a space or a comma at the end of an item vanished as it was
 * typed.
 *
 * So the textarea owns its raw text and the parsed array is published upward.
 * `lastEmitted` distinguishes our own echo from a genuine external change
 * (switching brand, the fetch landing), which is the only time the raw text
 * should be replaced under the cursor.
 */
function ListField({
  label,
  hint,
  value,
  onChange,
  rows = 3,
  separator = "lines",
}: {
  label: string;
  hint?: string;
  value: string[];
  onChange: (v: string[]) => void;
  rows?: number;
  separator?: "lines" | "commas";
}) {
  const joiner = separator === "lines" ? "\n" : ", ";
  const splitter = separator === "lines" ? "\n" : ",";

  const [text, setText] = useState(() => value.join(joiner));
  const lastEmitted = useRef<string[] | null>(null);

  useEffect(() => {
    // The parent stores exactly the array we handed it, so an identity match
    // means this render is our own edit coming back. Anything else is new data.
    if (value !== lastEmitted.current) setText(value.join(joiner));
  }, [value, joiner]);

  function handle(next: string) {
    setText(next);
    const parsed = next
      .split(splitter)
      .map((s) => s.trim())
      .filter(Boolean);
    lastEmitted.current = parsed;
    onChange(parsed);
  }

  return (
    <label className="block">
      <span className="label">{label}</span>
      <textarea
        value={text}
        rows={rows}
        onChange={(e) => handle(e.target.value)}
        // Tidies up on the way out: "a,,b " becomes "a, b" once you leave the
        // field, rather than fighting you while you are still typing it.
        onBlur={() => setText(value.join(joiner))}
        className="input resize-y"
      />
      {hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
}

/**
 * Tone as taps, plus room to say more.
 *
 * Tone was a blank text box, which is a hard question asked cold: most people
 * either leave it empty or write one adjective. The presets make the common
 * answers one tap, and the free-text box keeps the field open-ended, because
 * "warm, but never gushing" is a real answer no list of twelve contains.
 *
 * Both halves are stored in the one `tone` column, joined by formatTone and
 * split back by parseTone, so the database and the prompt are unchanged.
 */
function TonePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const { presets, extra } = parseTone(value);

  function toggle(preset: TonePreset) {
    const next = presets.includes(preset)
      ? presets.filter((p) => p !== preset)
      : [...presets, preset];
    onChange(formatTone(next, extra));
  }

  return (
    <div>
      <span className="label">{t("voice.tone")}</span>
      <div className="flex flex-wrap gap-1.5">
        {TONE_PRESETS.map((preset) => {
          const on = presets.includes(preset);
          return (
            <button
              key={preset}
              type="button"
              onClick={() => toggle(preset)}
              aria-pressed={on}
              className={`rounded-full border px-3 py-1.5 text-[13px] transition-colors ${
                on
                  ? "border-primary-600 bg-primary-600 text-white"
                  : "border-border bg-white text-muted hover:border-primary-300 hover:text-ink"
              }`}
            >
              {t(`voice.tones.${preset}`)}
            </button>
          );
        })}
      </div>
      <input
        value={extra}
        onChange={(e) => onChange(formatTone(presets, e.target.value))}
        placeholder={t("voice.tonePlaceholder")}
        className="input mt-2"
      />
      <span className="hint">{t("voice.toneHint")}</span>
    </div>
  );
}

function Choice({
  label,
  value,
  onChange,
  options,
  labelFor,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  labelFor: (o: string) => string;
}) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="select">
        {options.map((o) => (
          <option key={o} value={o}>
            {labelFor(o)}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The API returns nulls; the text inputs want empty strings.
 *
 * `pricePositioning` is exempt: it is an enum, so "" is not a legal value and
 * round-tripping an unanswered one through the form would fail on save.
 */
function stripNulls(row: Record<string, unknown>): Partial<BrandProfileInputDto> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (key === "updatedAt") continue;
    if (key === "pricePositioning" || key === "imagePeople") {
      out[key] = value ?? null;
      continue;
    }
    out[key] = value === null ? "" : value;
  }
  return out as Partial<BrandProfileInputDto>;
}
